<template>
  <div class="step1-container">
    <!-- 거래처 검색 UI (모달에서 가져온 UI) -->
    <div class="search-client-content">
      <div class="search-client-header">
        <div class="modal-title">{{ $t('workorder_search_client') }}<card-field-hint field="hospitalName" /></div>
      </div>
      
      <div class="search-input-container-wrapper">
        <div class="search-input-container" data-tour="s1-search">
          <i class="fa-solid fa-magnifying-glass"/>
          <input
            type="text"
            @keyup.enter="search"
            v-model="searchParam.condition"
            :placeholder="$t('workorder_search_placeholder')"
          >
          <button class="search" @click="search">{{ $t('workorder_search_btn') }}</button>
        </div>
      
        <div class="list-container" data-tour="s1-list">
          <div class="list-items" v-if="result.list.length > 0">
            <div
              v-for="(item, index) in result.list"
              :key="index"
              class="list-item"
              :class="item.isSelected ? 'selected' : ''"
              @click="chooseItem(index)"
            >
              <a-typography-text :ellipsis="true" style="width: 100%;">
                <div class="item-left">
                  {{ item.name }}<span class="cno">({{ item.convertedHospitalInfo }})</span>
                </div>
              </a-typography-text>
              <div class="item-right" v-if="item.isSelected">
                <i class="fa-solid fa-check"/>
              </div>
            </div>
          </div>
          <div
            v-else
            data-tour="s1-register"
            class="empty-with-register"
          >
            <no-result
              :colspan="10"
              :message="$t('workorder_search_no_result')"
              :height="'auto'"
              :noIcon="true"
            />
            <!-- 거래처를 못 찾은 사용자에게 등록 경로를 바로 연결 (SPEC-CHINA-WO-USAGE-GUIDE) -->
            <div class="register-cta-wrap">
              <span class="register-cta-hint">{{ $t('workorder_search_register_hint') }}</span>
              <button class="register-cta-btn" @click="openRegisterModal">
                <i class="fa-solid fa-plus" style="margin-right: 6px;"/>{{ $t('workorder_search_register_cta') }}
              </button>
            </div>
          </div>
          <div
            v-if="$store.state.isLoading !== false || result.list.length === 0"
            class="page-wrapper"
          />
          <pagination
            v-if="$store.state.isLoading === false && result.list.length > 0"
            :page-info="paginationData"
            page-name="Step1ClientSearch"
            :pagePerBlock="5"
            @change-page="paginationHandler"
          />
        </div>
      </div>
    </div>

    <!-- 거래처(병원) 등록 모달 — 병원 관리의 추가 폼을 위저드 내에서 재사용 -->
    <company-form-modal
      :open="registerModalOpen"
      type="HOSPITAL"
      @close="registerModalOpen = false"
      @saved="onHospitalRegistered"
    />
  </div>
</template>

<script>
import {
  businessNumberFormatter,
  phoneNumberFormatter,
  oneBtnDialog
} from '@/utils/utils';
import { formatCompanyAddress } from '@/utils/companyAddress';
import { chinaInapi } from '@/apis/chinaInapi';
import NoResult from '@/views/components/NoResult';
import Pagination from '@/views/components/Pagination';
import CompanyFormModal from '@/views/CompanyList/components/CompanyFormModal.vue';
import CardFieldHint from './CardFieldHint.vue';

export default {
  name: 'Step1',
  components: {
    NoResult,
    Pagination,
    CompanyFormModal,
    CardFieldHint
  },
  data() {
    return {
      searchParam: {
        condition: '',
      },
      paginationData: {
        listSize: 0,
        rowsPerPage: 10,
        currentPage: 1,
        totalPages: 0,
      },
      selectedItem: null,
      selectedClient: null,
      result: {
        list: [],
        totalDocs: 0,
      },
      isSearched: false,
      searchType: 'HOSPITAL',
      registerModalOpen: false
    }
  },
  computed: {
    formToParam() {
      // china-inapi GET /china/hospitals 쿼리.
      // 기공소 스코핑(viewDentalLabIds ∋ 기공소)은 서버가 JWT 기준으로 처리하므로
      // type/medicalDept/locale 클라이언트 전달 불필요(Model A m2m 허용목록).
      const obj = {
        page: this.paginationData.currentPage,
        limit: 10,
      };
      // 검색어 → name 부분검색(regex escape). cNo 검색은 china-inapi 미지원이라 name으로 통일.
      if (this.searchParam?.condition) {
        obj.name = this.searchParam.condition;
      }
      return obj;
    }
  },
  mounted() {
    // 초기 데이터 로드
    this.isSearched = true;
    this.getClientList();
  },
  methods: {
    formatBusinessNumber(value) {
      return businessNumberFormatter(value);
    },
    formatPhoneNumber(value) {
      return phoneNumberFormatter(value);
    },
    getAddressText(item) {
      return formatCompanyAddress(item);
    },
    joinHospitalInfo(item) {
      const array = [
        item.ceo,
        businessNumberFormatter(item.cNo),
      ];

      const address = formatCompanyAddress(item);
      if (address) array.push(address)

      return array.join(', ');
    },
    async search() {
      this.paginationData.currentPage = 1;
      this.isSearched = true;
      await this.getClientList();
    },
    async getClientList() {
      this.$store.commit('setIsLoading', true);
      try {
        // china-inapi: GET /china/hospitals (기공소 viewDentalLabIds 스코핑).
        // 응답 { data, total, page, limit, totalPages } — 한국 inapi { docs, totalDocs } 와 키 상이.
        const response = await chinaInapi.get('/china/hospitals', { params: this.formToParam });

        const list = response.data?.data ?? [];

        // (제거) 포인트임플란트 계정 한정 '서티치과' 필터 — 한국 admin 임시코드 잔재.
        // 해당 회사 ID(VUE_APP_POINT_IMPLANT)는 xinlaian companies에 존재하지 않아(0건 실측)
        // 중국에서는 도달 불가능한 죽은 분기였다(2026-08-13).
        this.result.list = list;

        this.result.totalDocs = response.data?.total ?? 0;
        this.paginationData.totalPages = response.data?.totalPages ?? 0;

         // 선택 상태 초기화 및 convertedHospitalInfo 생성
         this.result.list.forEach(item => {
           item.isSelected = false;
           item.convertedHospitalInfo = this.joinHospitalInfo(item);
         });
      } catch (error) {
        console.error('거래처 검색 실패:', error);
        await oneBtnDialog(this.$t('workorder_search_fail_title'), this.$t('workorder_search_fail_msg'), false);
      } finally {
        this.$store.commit('setIsLoading', false);
      }
    },
    chooseItem(index) {
      // 이미 선택된 항목이면 무시
      if (this.result.list[index].isSelected) return;

      // 모든 항목의 선택 상태 초기화
      this.result.list.forEach((item, i) => {
        item.isSelected = (index === i);
      });

      // 선택된 항목 저장
      this.selectedItem = this.result.list[index];
      this.selectedClient = this.selectedItem;
      
      // 부모 컴포넌트에 선택 이벤트 전달
      this.$emit('select-client', this.selectedItem);
    },
    async paginationHandler(childPassParam) {
      this.paginationData.currentPage = childPassParam.page;
      await this.getClientList();
    },
    // 선택 상태 초기화
    resetSelection() {
      this.selectedItem = null;
      this.result.list.forEach(item => {
        item.isSelected = false;
      });
    },
    clearSelection() {
      this.selectedClient = null;
      this.selectedItem = null;
      this.result.list.forEach(item => {
        item.isSelected = false;
      });
      this.$emit('select-client', null);
    },
    // 거래처 등록 모달 열기 (검색 결과 없음 → 병원 등록 진입점)
    openRegisterModal() {
      this.registerModalOpen = true;
    },
    // 병원 등록 완료 → 등록한 거래처 이름으로 재검색 후 자동 선택
    async onHospitalRegistered(payload) {
      this.registerModalOpen = false;
      const name = payload?.name;
      const createdId = payload?.record?._id || payload?.record?.data?._id || null;
      if (name) {
        this.searchParam.condition = name;
      }
      this.paginationData.currentPage = 1;
      this.isSearched = true;
      await this.getClientList();
      // 자동 선택: _id 우선 매칭, 없으면 이름 매칭
      let idx = -1;
      if (createdId) idx = this.result.list.findIndex(i => i._id === createdId);
      if (idx < 0 && name) idx = this.result.list.findIndex(i => i.name === name);
      if (idx >= 0) {
        this.chooseItem(idx);
        this.$message?.success?.(this.$t('workorder_search_register_success'));
      } else {
        this.$message?.info?.(this.$t('workorder_search_registered_not_found'));
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/search-client-modal.scss';

.step1-container {
  width: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);


  // 모달 스타일을 그대로 유지하되 인라인으로 적용
  .search-client-content {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background-color: white;
    border-radius: 14px;
    overflow: hidden;
    width: 100%;

    .search-client-header {
      width: 100%;
      padding: 0 20px;
      height: 52px;
      display: flex;
      align-items: center;
      justify-content: start;
      position: relative;
      border-bottom: 1px solid rgba(0, 0, 0, 0.10);
      color: #2F3337;
      font-size: 17px;
      font-weight: 700;
      padding: 20px;
    }

    .search-input-container-wrapper {
      width: 100%;
      padding: 10px 20px;

    }
    .search-input-container {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      border: 1px solid rgba(0, 0, 0, 0.20);
      padding: 0px 0px 0px 14px;
      overflow: hidden;
      

      i {
        color: #8D939A;
      }

      input {
        width: 100%;
        border: none;
        outline: none;
        min-height: 40px;
        padding: 0px 14px;

        &:focus {
          border: none;
          box-shadow: none;
        }

        &::placeholder {
          color: #8D939A;
        }
      }

      .search {
        overflow: hidden;
        width: 150px;
        background-color: #2F3337;
        color: white;
        border-top-left-radius: 0px;
        border-bottom-left-radius: 0px;
        font-size: 15px;
        font-weight: bold;
        border-radius: 0;
        height: 40px;
        padding: 0 20px;
        justify-content: center;
        align-items: center;
        border-radius: 8px;
      }

      color: #2F3337;
      box-shadow: none !important;
      border: 1px solid #D1D6DB;
      
      &:hover {
        outline: none;
        border: 1px solid #8D939A !important;
        box-shadow: none;
      }

      &:focus-within {
        outline: none;
        border: 1px solid #4B5158 !important;
        box-shadow: 0px 0px 0px 2px rgba(0, 0, 0, 0.15) !important;
      }
    }

    .list-container {
      width: 100%;
      min-height: 540px;
      border-radius: 8px;
      border: 1px solid rgba(0, 0, 0, 0.20);
      display: flex;
      overflow: hidden;
      align-items: center;
      flex-direction: column;
      justify-content: space-between;
      margin: 20px 0px;

      .list-items {
        width: 100%;
        overflow: hidden;

        .list-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          gap: 8px;
          height: 48px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.20);
          color: #2F3337;

          .cno {
            margin-left: 10px;
            color: #ACB2B9;
          }

          .item-right {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            border-radius: 100%;
            background-color: #ffe414;

            i {
              font-size: 14px;
            }
          }

          &:hover {
            cursor: pointer;
          }
        }

        .list-item.selected {
          font-weight: 700;
          background-color: #FFFAE0;

          .cno {
            font-weight: 700;
          }
        }
      }
    }
  }

  .expiration-modal-footer {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: end;
    padding: 0 20px 12px 20px;
    gap: 10px;

    .contract-submit-btn {
      font-size: 15px;
      font-weight: bold;
      background-color: #ffe414;
      color: #2F3337;
      height: 40px;
      padding: 0 20px;
      justify-content: center;
      align-items: center;
      border-radius: 8px;
    }

    .contract-cancel-btn {
      font-size: 15px;
      font-weight: bold;
      color: #6E747C;
      height: 40px;
      padding: 0 20px;
      justify-content: center;
      align-items: center;
      border-radius: 8px;
    }
  }

  .empty-with-register {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-top: 130px;

    .register-cta-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;

      .register-cta-hint {
        color: #8D939A;
        font-size: 14px;
      }

      .register-cta-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 40px;
        padding: 0 22px;
        border-radius: 8px;
        background-color: #ffe414;
        color: #2F3337;
        font-size: 15px;
        font-weight: 700;
        border: none;
        cursor: pointer;

        &:hover {
          background-color: #ffdb00;
        }
      }
    }
  }

  .page-wrapper {
    width: 100%;
    background-color: #F3F5F6;
    display: flex;
    align-items: center;
    margin-top: 0px;
    padding-top: 20px;
    height: 60px;
  }
}

@media (max-width: 768px) {
  .step1-container {
    .search-client-content {
      .search-input-container {
        margin: 16px;
      }

      .list-container {
        margin: 0 16px;
        min-height: 400px;
      }
    }

    .expiration-modal-footer {
      padding: 0 16px 12px 16px;
      flex-direction: column;
      gap: 8px;

      .contract-cancel-btn,
      .contract-submit-btn {
        width: 100%;
      }
    }
  }
}
</style>